
import React, { forwardRef } from "react";
import { UserProduct, UserCategory } from "@/types";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import ProductImage from "@/components/products/ProductImage";

interface ProductGridProps {
  groupedProducts: Record<string, UserProduct[]>;
  categories: UserCategory[];
  onProductSelect: (product: UserProduct) => void;
  addedProductIds: string[];
  checkedProductIds: string[];
  recentlyAddedIds: string[];
  style?: React.CSSProperties;
}

// Wrappa con forwardRef per permettere scrollRef dall'esterno
const ProductGrid = forwardRef<HTMLDivElement, ProductGridProps>(({ 
  groupedProducts,
  categories,
  onProductSelect,
  addedProductIds,
  checkedProductIds,
  recentlyAddedIds,
  style
}, ref) => {
  const { theme } = useThemeConfig();
  return (
    <div ref={ref} className="space-y-6" style={style}>
      {Object.keys(groupedProducts).map((groupKey) => {
        const defaultCategory: UserCategory = {
          id: "other",
          userId: "",
          name: "Altri Prodotti",
          icon: "📦",
          color: "#f0f0f0",
          textColor: "#000000",
          position: 0,
          visibile: true
        };

        const category: UserCategory =
          groupKey === "other"
            ? defaultCategory
            : categories.find(cat => cat.id === groupKey) || defaultCategory;

        return (
          <div key={groupKey}>
            <div
              className="flex items-center px-3 py-2 rounded-md"
              style={{
                backgroundColor: category.color,
                color: category.textColor,
              }}
            >
              <span className="mr-2">{category.icon}</span>
              <span className="font-bold">{category.name}</span>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-2">
              {groupedProducts[groupKey].map((product: UserProduct) => {
                const isChecked = checkedProductIds.includes(product.id);
                const isRecentlyAdded = recentlyAddedIds.includes(product.id);
                const isAdded = addedProductIds.includes(product.id) && !isChecked;

                return (
                  <div
                    key={product.id}
                    className={`p-2 border rounded-md cursor-pointer hover:bg-[var(--cardBackgroundColor)]`}
                    style={{
                      backgroundColor: isRecentlyAdded
                        ? theme.successColor
                        : isChecked
                        ? theme.errorColor
                        : isAdded
                        ? theme.successColor
                        : theme.backgroundColor,
                      color:
                        isChecked || isAdded || isRecentlyAdded
                          ? theme.textPrimaryColor
                          : theme.textColor,
                    }}
                    onClick={() => onProductSelect(product)}
                  >
                    <div className="flex items-start justify-between">
                      <p className="text-sm font-semibold break-words mr-2 flex-1">
                        {product.name}
                      </p>
                      {product.photo && (
                        <ProductImage
                          productId={product.id}
                          photo={product.photo}
                          alt={product.name}
                          className="w-4 h-4 object-cover flex-shrink-0"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default ProductGrid;
